<script setup lang="ts">
// Public page (auth-optional: never redirects, stays server-rendered for SEO,
// but the session resolves so the header shows the menu + balances when signed
// in). Two boards for the day before (2AM–2AM America/Vancouver): top winners
// (gross BMO) and sharpest (winning predictions). Only display names are ever
// shown — never emails.
definePageMeta({ middleware: 'auth-optional' })

interface Row { rank: number; name: string; winnings: number; correct: number; value: number }
interface Board {
  metric: string; day: string; window_start: string; window_end: string
  page: number; page_size: number; total: number; items: Row[]
}

const api = useApi()
const route = useRoute()
const router = useRouter()
const { fmtMoney } = useClientFormat()

// Read ONCE at setup (never a computed off route.query) so the SSR render and
// the hydration render see identical input — same idiom as pages/dfs/index.vue.
const queryMetric = String(route.query.metric || '')
const metric = ref<'winnings' | 'correct'>(queryMetric === 'correct' ? 'correct' : 'winnings')
const queryPage = Number(route.query.page || 1)
const page = ref(Number.isFinite(queryPage) && queryPage > 0 ? Math.floor(queryPage) : 1)

const { data: board, pending } = await useAsyncData<Board | null>('leaderboard',
  () => api.get<Board>(`/leaderboards?metric=${metric.value}&page=${page.value}`)
    .catch(() => null),
  { watch: [metric, page] })

// replace, not push: flipping boards/pages shouldn't stack history entries, but
// the URL carries the state so back-nav and deep links restore it
function syncQuery() {
  router.replace({ query: { ...route.query, metric: metric.value, page: String(page.value) } })
}
function setMetric(m: 'winnings' | 'correct') {
  if (metric.value === m) return
  metric.value = m
  page.value = 1
  syncQuery()
}

const totalPages = computed(() => {
  const b = board.value
  if (!b || !b.page_size) return 1
  return Math.max(1, Math.ceil(b.total / b.page_size))
})
function prev() { if (page.value > 1) { page.value--; syncQuery() } }
function next() { if (page.value < totalPages.value) { page.value++; syncQuery() } }

function medal(rank: number) {
  return rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : ''
}
</script>

<template>
  <div>
    <AppNav title="leaders" />

    <div class="wrap narrow">
      <p class="sub">Yesterday's results · the daily board resets at 2:00 AM Pacific.</p>

      <div class="tabs">
        <button class="tab" :class="{ active: metric === 'winnings' }" @click="setMetric('winnings')">
          most BMO won
        </button>
        <button class="tab" :class="{ active: metric === 'correct' }" @click="setMetric('correct')">
          most correct
        </button>
      </div>

      <div v-if="pending" class="loading">loading…</div>
      <div v-else-if="!board || !board.items.length" class="loading">
        no results for the day before yet — check back after games settle.
      </div>

      <div v-else class="lb">
        <div v-for="(r, i) in board.items" :key="i" class="lb-row" :class="{ top: r.rank <= 3 }">
          <div class="lb-rank mono">
            <span v-if="medal(r.rank)" class="medal">{{ medal(r.rank) }}</span>
            <span v-else>#{{ r.rank }}</span>
          </div>
          <div class="lb-name">{{ r.name }}</div>
          <div class="lb-val mono">
            <template v-if="metric === 'winnings'">{{ fmtMoney(r.value) }} <span class="u">BMO</span></template>
            <template v-else>{{ r.value }} <span class="u">correct</span></template>
          </div>
        </div>

        <div class="pager" v-if="totalPages > 1">
          <button class="btn-ghost" :disabled="page <= 1" @click="prev">← prev</button>
          <span class="pg mono">page {{ board.page }} / {{ totalPages }}</span>
          <button class="btn-ghost" :disabled="page >= totalPages" @click="next">next →</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.narrow { max-width: 680px; }
.sub { color: var(--muted); font-size: 13px; margin: 4px 0 18px; }
.loading { color: var(--muted); text-align: center; padding: 40px 20px; }
.lb-row { display: grid; grid-template-columns: 54px 1fr auto; align-items: center; gap: 12px;
  padding: 13px 16px; border: 1px solid var(--line); border-radius: 11px; margin-bottom: 9px;
  background: var(--panel); }
.lb-row.top { border-color: var(--brand); background: linear-gradient(180deg, rgba(219,86,152,0.08), var(--panel)); }
.lb-rank { color: var(--muted); font-weight: 700; }
.lb-rank .medal { font-size: 20px; }
.lb-name { font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lb-val { text-align: right; color: var(--accent); font-weight: 700; }
.lb-val .u { font-size: 10px; color: var(--muted); text-transform: uppercase; }
.pager { display: flex; align-items: center; justify-content: center; gap: 16px; margin-top: 18px; }
.pager .pg { color: var(--muted); font-size: 12px; }
.pager .btn-ghost { width: auto; }
</style>
